import { useState } from 'react'
import { Link } from 'react-router-dom'
import { sendRecoveryEmail } from '@/utils/api'
import PublicLayout from '@/components/layout/PublicLayout'

export default function KeyRecovery({ embedded }: { embedded?: boolean }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setStatus('sending')
    setError('')

    try {
      const result = await sendRecoveryEmail(email.trim().toLowerCase())
      if (result.success) {
        setStatus('sent')
      } else {
        setStatus('error')
        setError('Failed to send recovery email. Please try again.')
      }
    } catch {
      setStatus('error')
      setError('Network error. Please check your connection and try again.')
    }
  }

  const Wrapper = embedded ? ({ children }: { children: React.ReactNode }) => <>{children}</> : PublicLayout

  if (status === 'sent') {
    return (
      <Wrapper>
        <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
          <div className="w-full max-w-3xl">
            <div className="text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 rounded-full bg-[var(--bac-safe)] mx-auto mb-6 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
                Check Your Email
              </h1>

              <p className="text-[var(--text-secondary)] mb-6">
                If an account exists with <strong>{email}</strong>, we've sent you a recovery email with your unique URL.
              </p>

              <p className="text-sm text-[var(--text-secondary)] mb-8">
                Don't see it? Check your spam folder or try again with a different email address.
              </p>

              <div className="flex flex-col gap-3 max-w-md mx-auto">
                <button
                  onClick={() => {
                    setStatus('idle')
                    setEmail('')
                  }}
                  className="btn btn-secondary"
                >
                  Try Different Email
                </button>
                <Link to="/" className="btn btn-primary">
                  Return Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
        <div className="w-full max-w-3xl space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <svg className="w-12 h-12 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Key Recovery</h1>
            <p className="text-[var(--text-secondary)] mt-2">Recover your passcode via email</p>
          </div>

          {/* Instructions */}
          <div className="card">
            <h2 className="font-semibold text-[var(--text-primary)] mb-3">
              How it works
            </h2>
            <ol className="text-sm text-[var(--text-secondary)] space-y-2 list-decimal list-inside">
              <li>Enter the email address you previously registered</li>
              <li>We'll send you a recovery email with your unique URL</li>
              <li>Click the link to access your drink log</li>
            </ol>
            <p className="text-xs text-[var(--text-secondary)] mt-4">
              Note: This only works if you previously set up a backup email in Settings.
            </p>
          </div>

          {/* Recovery Form */}
          <div className="card">
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setError('')
                  }}
                  placeholder="you@example.com"
                  className="w-full"
                  disabled={status === 'sending'}
                />
              </div>

              {error && (
                <p className="text-sm text-[var(--bac-danger)] mb-4">{error}</p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full btn btn-primary disabled:opacity-50"
              >
                {status === 'sending' ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="spinner" />
                    Sending...
                  </span>
                ) : (
                  'Send Recovery Email'
                )}
              </button>
            </form>
          </div>

          {/* E2E Encryption Note */}
          <div className="p-3 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg">
            <p className="text-sm text-[var(--accent-orange)]">
              Key recovery will send you your passcode URL, but if you have end-to-end encryption enabled, you will still need your password to decrypt your data.
            </p>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}
